import { ChevronDownIcon, MinusIcon, XMarkIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import useDataTableContext from "../../../../../../context/useDataTableContext";
import { getUsedFootnotes } from "../../../../../../helpers";
import { DataTableCell, DataType } from "../../../../../../Models";

export interface DataTableReadOnlyCellProps {
    rowId: string;
    cell: DataTableCell;
    hasInfo: boolean;
    isInfoVisible: boolean;
    toggleInfo: () => void;
}

const CircleIcon = (props: React.SVGProps<SVGSVGElement>) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={1.5}
        stroke="currentColor"
        {...props}
    >
        <path strokeLinecap="round" strokeLinejoin="round" d="M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
    </svg>
);

const ReadOnlyCell = ({ rowId, cell, hasInfo, isInfoVisible, toggleInfo }: DataTableReadOnlyCellProps) => {
    const { state } = useDataTableContext();
    const { columns, rows, footnotes, onFilterColumn, activeDisciplines } = state;

    const renderFootnotes = () => {
        const usedFootnotes = getUsedFootnotes(rows, footnotes, true);
        return cell.footnotes?.map((x) => usedFootnotes.findIndex((y) => y.id === x) + 1).join(", ");
    };

    const { id, value } = cell;
    const row = rows.find((x) => x.id === rowId)!;
    const columnId = row.cells.findIndex((x) => x.id === id);
    const column = columns[columnId];

    if (!column) {
        console.warn(`Column not found for cell with id: ${id} in row with id: ${rowId}`, cell);
        return null;
    }

    const cellDataType = column.dataType;

    const renderValue = () => {
        const element = document.createElement("template");
        element.innerHTML = value;

        if (column.dataType === DataType.BOOLEAN) {
            let Icon = <XMarkIcon className="w-5 h-5 mx-auto text-primary-500" />;

            if (row.metadata?.propertySymbol && row.metadata.propertySymbol !== "X")
                Icon = <CircleIcon className="w-5 h-5 mx-auto text-primary-500" />;

            if (Array.isArray(cell.metadata?.disciplines)) {
                const attachedDisciplines: Array<string> = cell.metadata.disciplines;
                if (activeDisciplines && activeDisciplines.length > 0) {
                    if (attachedDisciplines.some((disciplineId) => activeDisciplines.includes(disciplineId))) {
                        return (
                            <>
                                {Icon}
                                <sup>{renderFootnotes()}</sup>
                            </>
                        );
                    }
                } else if (attachedDisciplines.length > 0) {
                    return (
                        <>
                            {Icon}
                            <sup>{renderFootnotes()}</sup>
                        </>
                    );
                }
            }

            return null;
        }

        if (row.metadata?.propertySymbol as string) {
            element.innerHTML = `<strong>${element.innerHTML}</strong>`;
        }

        const allLinks = element.content.querySelectorAll("[href]");
        for (let i = 0; i < allLinks.length; i++) {
            const link = allLinks[i] as HTMLLinkElement;
            link.target = link.href.startsWith(window.location.origin) ? "_self" : "_blank";
        }

        if (cell.footnotes?.length > 0) {
            if ((cell.metadata?.classifications as string)?.length > 0) {
                // Ignore these for now, we handle them separately later
            } else {
                const lastChild = element.content.lastElementChild;
                if (lastChild && lastChild.nodeType !== 1) {
                    lastChild.innerHTML += `<sup>${renderFootnotes()}</sup>`;
                } else {
                    element.innerHTML += `<sup>${renderFootnotes()}</sup>`;
                }
            }
        }

        return <span className="mr-auto" dangerouslySetInnerHTML={{ __html: element.innerHTML }} />;
    };

    const renderClassificationsFootnotes = () => {
        if ((cell.metadata?.classifications as string)?.length > 0) {
            return (
                <div className="mb-auto ml-auto">
                    <sup>{renderFootnotes()}</sup>
                </div>
            );
        }

        return null;
    };

    if (onFilterColumn && onFilterColumn(column)) return null;

    const renderContent = () => {
        return (
            <div className="flex items-center break-normal">
                {renderValue()}
                {renderClassificationsFootnotes()}
                {hasInfo &&
                    (isInfoVisible ? (
                        <MinusIcon className="w-5 h-5 text-primary-500 cursor-pointer" onClick={() => toggleInfo()} />
                    ) : (
                        <ChevronDownIcon
                            className="w-5 h-5 text-primary-500 cursor-pointer"
                            onClick={() => toggleInfo()}
                        />
                    ))}
            </div>
        );
    };

    return columnId === 0 ? (
        <th
            scope="row"
            className={clsx([
                "py-3 px-2.5 border-l border-b border-[#E5E5E5] font-medium last:border-r",
                "break-all",
                isInfoVisible ? "border-b-0" : "border-b",
                cellDataType === DataType.BOOLEAN && "text-center",
            ])}
        >
            {renderContent()}
        </th>
    ) : (
        <td
            scope="row"
            className={clsx([
                "py-3 px-2.5 border-l border-b border-[#E5E5E5] font-medium last:border-r",
                "break-all",
                isInfoVisible ? "border-b-0" : "border-b",
                cellDataType === DataType.BOOLEAN && "text-center",
            ])}
        >
            {renderContent()}
        </td>
    );
};

export default ReadOnlyCell;
